import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FileText, Download, Loader2, BookOpen, Briefcase, Gift } from 'lucide-react';
import PortalLayout from './PortalLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const muted = { color: 'var(--p-muted)' };

const ResourceCard = ({ r }) => {
  // Members are already opted in. Download directly, no lead form.
  const href = r.download_url || (r.slug ? `/resources/${r.slug}` : '#');
  return (
    <div className="p-card rounded-2xl p-6 flex flex-col" data-testid={`resource-${r.slug || r.resource_id}`}>
      <div className="flex items-start gap-3 mb-3">
        <span className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(29,78,216,0.1)', color: '#1D4ED8' }}>
          <FileText size={20} />
        </span>
        <h3 className="text-lg font-semibold leading-snug" style={{ fontFamily: 'Outfit, sans-serif' }}>{r.title}</h3>
      </div>
      {(r.description || r.short_description) && (
        <p className="text-sm mb-5 flex-grow" style={muted}>{r.short_description || r.description}</p>
      )}
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        download
        className="mt-auto inline-flex items-center justify-center gap-2 bg-[#1D4ED8] hover:bg-[#2563EB] text-white px-5 py-2.5 rounded-lg font-medium text-sm transition-colors"
        data-testid={`resource-download-${r.slug || r.resource_id}`}
      >
        <Download size={15} /> {r.cta_label || 'Download'}
      </a>
    </div>
  );
};

const Section = ({ icon: Icon, eyebrow, title, blurb, items, testid }) => {
  if (!items.length) return null;
  return (
    <section className="mb-12" data-testid={testid}>
      <div className="flex items-center gap-2 mb-1">
        <Icon size={18} className="text-[#F97316]" />
        <span className="text-xs tracking-[0.18em] uppercase font-semibold text-[#F97316]">{eyebrow}</span>
      </div>
      <h2 className="text-2xl font-light tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>{title}</h2>
      {blurb && <p className="text-sm mt-1 mb-5 max-w-2xl" style={muted}>{blurb}</p>}
      <div className="grid sm:grid-cols-2 gap-5">
        {items.map((r) => <ResourceCard key={r.resource_id} r={r} />)}
      </div>
    </section>
  );
};

export default function ClientResources() {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/api/portal/resources`, { withCredentials: true })
      .then((r) => setResources(Array.isArray(r.data) ? r.data : []))
      .catch(() => setResources([]))
      .finally(() => setLoading(false));
  }, []);

  const business = resources.filter((r) => r.category === 'business');
  const free = resources.filter((r) => r.category !== 'business');

  return (
    <PortalLayout active="resources">
      <div className="mb-8" data-testid="client-resources-page">
        <span className="text-xs tracking-[0.2em] uppercase text-[#F97316] font-semibold p-eyebrow">Member Resources</span>
        <h1 className="text-3xl sm:text-4xl tracking-tight font-light mt-1" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="resources-title">
          Resource Library
        </h1>
        <p className="mt-2 max-w-2xl" style={muted}>Everything in one place: your exclusive member resources plus all of our free guides. As a member, downloads are instant; no forms to fill out.</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#1D4ED8]" size={28} /></div>
      ) : resources.length === 0 ? (
        <div className="p-card rounded-2xl p-10 text-center" data-testid="no-resources">
          <BookOpen size={36} className="mx-auto mb-3" style={{ color: 'var(--p-faint)' }} />
          <p className="text-sm" style={muted}>No resources published yet. Check back soon.</p>
        </div>
      ) : (
        <>
          <Section
            icon={Briefcase}
            eyebrow="Members Only"
            title="Business Resources"
            blurb="Premium guides, templates and toolkits we've created for DCA members."
            items={business}
            testid="business-resources"
          />
          <Section
            icon={Gift}
            eyebrow="Always Free"
            title="Free Resources"
            blurb="The free guides and checklists we share with everyone, yours to keep."
            items={free}
            testid="free-resources"
          />
        </>
      )}
    </PortalLayout>
  );
}
